import React, { useEffect } from 'react';
import styled from 'styled-components';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import { IntlShape, injectIntl } from 'react-intl';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardTitle from 'containers/common/Card/CardTitle';
import { resetStoreClaim } from 'containers/RecapManagerPage/actions';
import { resetSearchClaimsStore } from 'containers/IdentificationPage/actions';
import { createStructuredSelector } from 'reselect';
import { connect } from 'react-redux';
import moment from 'moment-timezone';
import TablePagination from '@material-ui/core/TablePagination';

import { translate } from 'containers/services/translate';
import messages from 'containers/ManagerPendingClaims/messages';
import { pushTo } from 'containers/services/routes-helper';
import { PAGES } from 'routes';
import { requestManagerPendingClaims } from 'containers/ManagerPendingClaims/actions';
import {
  makeSelectPendingClaims,
  makeSelectCountPendingClaims,
} from 'containers/ManagerPendingClaims/selectors';
import { CLAIM_STATUS } from 'containers/data-structure';
import * as featureService from 'features/fflip-helpers';
import { FEATURES } from 'features/features';
import { ROW_PER_PAGE } from 'constants/global';
import { ClaimByStatus, SagisConfig } from 'containers/AppContainer/typings';
import { makeSelectListingsByField } from 'containers/AppContainer/selectors';
import { LISTINGS } from 'containers/data-structure';
import { formatClaim } from 'containers/AppContainer/utils';
import NewSearchBar from 'components/NewSearchBar';
import {
  SEARCH_QUERY_INIT_VALUES,
  SEARCH_TYPE_IDS,
} from 'containers/ManagerPendingClaims/constants';
import { SEARCH_TYPE } from 'containers/ValidatedClaimsPage/constants';
import { requestSearch } from 'containers/ValidatedClaimsPage/actions';

const Container = styled.div`
  margin-top: 50px;
  padding-top: 0px;

  .title {
    text-align: left;
    font-family: Roboto;
    font-weight: Bold;
    font-size: 18px;
    letter-spacing: 0;
    color: #384264;
    opacity: 1;
    padding: 10px;
  }
  .headerTitle {
    text-align: left;
    font-size: 11px;
    letter-spacing: 0;
    color: #000000de;
    text-transform: none;
    opacity: 1;
    font-family: Roboto;
    padding-bottom: 0px;
    font-weight: Bold;
  }
  .row {
    border-radius: 4px;
    text-align: left;
    font-family: Roboto;
    letter-spacing: 0;
    font-size: 11px;
    color: #2b2727;
  }
  .claims {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
  }
  .add-button {
    cursor: 'pointer';
  }
  .header {
    display: flex;
    justify-content: space-between;
  }
`;

export interface ManagerPendingClaimsProps {
  intl: IntlShape;
  requestManagerPendingClaims: Function;
  pendingClaims: ClaimByStatus[];
  countClaims: number;
  cities: SagisConfig[];
  resetSearchClaimsStore: Function;
  resetStoreClaim: Function;
  requestSearch: Function;
}

export interface SearchQuery {
  lossDate: string | null;
  registrationNumber: string | null;
}

export const ManagerPendingClaims: React.FC<ManagerPendingClaimsProps> = (
  props,
): JSX.Element => {
  const [page, setPage] = React.useState(0);
  const [pageSize, setRowsPerPage] = React.useState(25);
  const [searchQuery, setSearchQuery] = React.useState<SearchQuery>(
    SEARCH_QUERY_INIT_VALUES,
  );

  const handlerSearch = () => {
    const query = {
      searchCriteria: {
        ...searchQuery,
        status: CLAIM_STATUS.PENDING,
      },
      searchType: SEARCH_TYPE.CLAIM,
    };
    props.requestSearch(query);
  };

  const handlerRefresh = () => {
    setSearchQuery({ ...SEARCH_QUERY_INIT_VALUES });
    props.requestManagerPendingClaims(CLAIM_STATUS.PENDING, page, pageSize);
  };

  useEffect(() => {
    props.resetSearchClaimsStore();
    props.resetStoreClaim();
    props.requestManagerPendingClaims(CLAIM_STATUS.PENDING, page, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPage(newPage);
    props.requestManagerPendingClaims(CLAIM_STATUS.PENDING, newPage, pageSize);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const size = parseInt(event.target.value, 10);
    setRowsPerPage(size);
    props.requestManagerPendingClaims(CLAIM_STATUS.PENDING, page, size);
  };

  const formattedPendingClaims = formatClaim(
    props.pendingClaims,
    props.cities as SagisConfig[],
  );

  const searchCriterias = [
    {
      id: SEARCH_TYPE_IDS.REGISTRATION_NUMBER,
      title: translate(messages.registrationNumberCriteria, props),
    },
  ];

  return (
    <Container>
      <Card>
        <CardContent>
          <div className="header">
            <CardTitle
              count={props.countClaims}
              message={translate({ ...messages.title }, props)}
            />
            {featureService.hasFeature(FEATURES.SEARCH_BAR) && (
              <NewSearchBar
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                handlerSearch={handlerSearch}
                handlerRefresh={handlerRefresh}
                isHomePage
                showAll={false}
                customCriterias={searchCriterias}
              />
            )}
          </div>
          <div>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell align="left" className="headerTitle">
                    {translate({ ...messages.claimNumber }, props)}
                  </TableCell>
                  <TableCell align="left" className="headerTitle">
                    {translate({ ...messages.registrationNumber }, props)}
                  </TableCell>
                  <TableCell align="left" className="headerTitle">
                    {translate({ ...messages.openingDate }, props)}
                  </TableCell>
                  <TableCell align="left" className="headerTitle">
                    {translate({ ...messages.lossDate }, props)}
                  </TableCell>
                  <TableCell align="left" className="headerTitle">
                    {translate({ ...messages.claimPlace }, props)}
                  </TableCell>
                  <TableCell align="left" className="headerTitle">
                    {translate({ ...messages.numberOfVictim }, props)}
                  </TableCell>
                  <TableCell align="left" className="headerTitle">
                    {translate({ ...messages.lawyer }, props)}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {formattedPendingClaims.map(row => (
                  <TableRow
                    key={row.id}
                    onClick={pushTo(`${PAGES.MANAGER_RECAP_PAGE}/${row.id}`)}
                  >
                    <TableCell align="left" className="row">
                      {row.claimNumber}
                    </TableCell>
                    <TableCell align="left" className="row">
                      {row.registrationNumber}
                    </TableCell>
                    <TableCell align="left" className="row">
                      {moment(row.createdAt).format('YYYY-MM-DD')}
                    </TableCell>
                    <TableCell align="left" className="row">
                      {moment(row.lossDate).format(
                        `YYYY-MM-DD - ${row.lossHour}`,
                      )}
                    </TableCell>
                    <TableCell align="left" className="row">
                      {row.lossLocationLabel}
                    </TableCell>
                    <TableCell align="left" className="row">
                      {row.victims.length}
                    </TableCell>
                    <TableCell align="left" className="row">
                      {row.createdBy}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={ROW_PER_PAGE}
              component="div"
              count={props.countClaims}
              rowsPerPage={pageSize}
              page={page}
              labelRowsPerPage={translate({ ...messages.linesPerPage }, props)}
              onChangePage={handleChangePage}
              onChangeRowsPerPage={handleChangeRowsPerPage}
            />
          </div>
        </CardContent>
      </Card>
    </Container>
  );
};

const mapDispatchToProps = {
  requestManagerPendingClaims,
  requestSearch,
  resetSearchClaimsStore,
  resetStoreClaim,
};

const mapStateToProps = createStructuredSelector({
  pendingClaims: makeSelectPendingClaims(),
  countClaims: makeSelectCountPendingClaims(),
  cities: makeSelectListingsByField(LISTINGS.CITIES),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(injectIntl(ManagerPendingClaims));
